import React, { useEffect, useRef, useState, useMemo } from "react";
import { getGanttData, getActivityView } from "../../api/process";
import SidePanel from "../../components/common/layout/SidePanel";
import ActivityView from "../../components/process/ActivityView";
import { Calendar, BarChart3, Maximize2, X, ChevronLeft, ChevronRight, Clock, AlertTriangle, CheckCircle2, TrendingUp } from "lucide-react";

const Construction = ({ projectId }) => {
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  const expandedGanttRef = useRef(null);
  const expandedGanttInstance = useRef(null);

  const [data, setData] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedActivityview, setSelectedActivityview] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartExpanded, setIsChartExpanded] = useState(false);

  // 🆕 뷰 모드: "timeline" (타임라인 카드) | "gantt" (간트 차트)
  const [displayMode, setDisplayMode] = useState("timeline");
  const [ganttViewMode, setGanttViewMode] = useState("Month");

  // 🆕 타임라인 뷰용 현재 월
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // 데이터 로드
  useEffect(() => {
    setIsLoading(true);
    getGanttData(projectId)
      .then((res) => {
        const safeData = res.map((task) => ({
          ...task,
          dependencies: Array.isArray(task.dependencies) ? task.dependencies : [],
        }));
        setData(safeData);

        // 첫 번째 활동의 시작월로 이동
        if (safeData.length > 0 && safeData[0].start) {
          const firstDate = new Date(safeData[0].start);
          setCurrentMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
        }
      })
      .catch((err) => console.error("공정 정보 불러오기 실패", err))
      .finally(() => setIsLoading(false));
  }, [projectId]);

  // 간트 차트 초기화 (displayMode가 gantt일 때만)
  useEffect(() => {
    if (displayMode !== "gantt" || !Array.isArray(data) || data.length === 0) return;
    if (!ganttRef.current) return;

    ganttRef.current.innerHTML = "";
    ganttInstance.current = new window.Gantt(ganttRef.current, data, {
      view_mode: ganttViewMode,
      readonly: true,
      show_arrows: false,
      bar_height: 24,
      bar_corner_radius: 4,
      padding: 16,
      custom_popup_html: null,
      on_click: (task) => handleActivityClick(task),
    });
  }, [data, ganttViewMode, displayMode]);

  // 확대 간트 차트
  useEffect(() => {
    if (!isChartExpanded || !Array.isArray(data) || data.length === 0) return;
    if (!expandedGanttRef.current) return;

    expandedGanttRef.current.innerHTML = "";
    expandedGanttInstance.current = new window.Gantt(expandedGanttRef.current, data, {
      view_mode: ganttViewMode,
      readonly: true,
      show_arrows: false,
      bar_height: 28,
      bar_corner_radius: 4,
      padding: 20,
      custom_popup_html: null,
      on_click: (task) => handleActivityClick(task),
    });
  }, [data, ganttViewMode, isChartExpanded]);

  // 활동 클릭 핸들러
  const handleActivityClick = async (task) => {
    try {
      setSelectedActivity(task);
      const detail = await getActivityView(task.id);
      setSelectedActivityview(detail);
      setIsPanelOpen(true);
    } catch (e) {
      console.error("활동 상세 불러오기 실패", e);
    }
  };

  // 🆕 월별 그룹화된 데이터
  const groupedByMonth = useMemo(() => {
    const groups = {};
    data.forEach((task) => {
      const start = new Date(task.start);
      const end = new Date(task.end);

      // 시작월과 종료월 사이의 모든 월에 추가
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

      while (current <= endMonth) {
        const key = `${current.getFullYear()}-${current.getMonth()}`;
        if (!groups[key]) {
          groups[key] = { year: current.getFullYear(), month: current.getMonth(), tasks: [] };
        }
        groups[key].tasks.push(task);
        current.setMonth(current.getMonth() + 1);
      }
    });
    return Object.values(groups).sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.month - b.month
    );
  }, [data]);

  // 현재 월의 활동들
  const currentMonthTasks = useMemo(() => {
    const key = `${currentMonth.year}-${currentMonth.month}`;
    const group = groupedByMonth.find(g => `${g.year}-${g.month}` === key);
    return group?.tasks || [];
  }, [groupedByMonth, currentMonth]);

  // 월 이동
  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = prev.month + direction;
      if (newMonth < 0) return { year: prev.year - 1, month: 11 };
      if (newMonth > 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: newMonth };
    });
  };

  // 상태 계산
  const getTaskStatus = (task) => {
    const progress = Number(task.progress) || 0;
    const today = new Date();
    const end = new Date(task.end);

    if (progress >= 100) return { label: "완료", color: "bg-green-500", bgColor: "bg-green-50", textColor: "text-green-700", icon: CheckCircle2 };
    if (today > end && progress < 100) return { label: "지연", color: "bg-red-500", bgColor: "bg-red-50", textColor: "text-red-700", icon: AlertTriangle };
    if (progress > 0) return { label: "진행중", color: "bg-blue-500", bgColor: "bg-blue-50", textColor: "text-blue-700", icon: TrendingUp };
    return { label: "예정", color: "bg-gray-400", bgColor: "bg-gray-50", textColor: "text-gray-600", icon: Clock };
  };

  // 날짜 포맷
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const monthNames = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">

        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">공정표 조회</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {data.length > 0 ? `${data.length}개 활동` : "데이터 로딩 중..."}
              </p>
            </div>
          </div>
        </div>

        {/* 🆕 뷰 모드 토글 */}
        <div className="bg-white rounded-lg border p-2">
          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1 flex-1">
              <button
                onClick={() => setDisplayMode("timeline")}
                className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${displayMode === "timeline"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500"
                  }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                타임라인
              </button>
              <button
                onClick={() => setDisplayMode("gantt")}
                className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${displayMode === "gantt"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500"
                  }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                간트
              </button>
            </div>

            {/* 간트 모드일 때 확대 버튼 */}
            {displayMode === "gantt" && (
              <button
                onClick={() => setIsChartExpanded(true)}
                disabled={data.length === 0}
                className="flex-shrink-0 w-9 h-9 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center justify-center transition-all active:scale-95 disabled:opacity-50"
              >
                <Maximize2 className="w-4 h-4 text-blue-600" />
              </button>
            )}
          </div>
        </div>

        {/* 로딩 상태 */}
        {isLoading ? (
          <div className="bg-white rounded-lg p-12 border flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm text-gray-500">공정표 로딩 중...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-lg p-12 border flex flex-col items-center">
            <Calendar className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">공정 데이터가 없습니다.</p>
          </div>
        ) : displayMode === "timeline" ? (
          /* 🆕 타임라인 카드 뷰 */
          <>
            {/* 월 네비게이션 */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-3 text-white">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors active:scale-95"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <div className="text-lg font-bold">{currentMonth.year}년 {monthNames[currentMonth.month]}</div>
                  <div className="text-xs text-white/80">{currentMonthTasks.length}개 활동</div>
                </div>
                <button
                  onClick={() => navigateMonth(1)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors active:scale-95"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 활동 카드 리스트 */}
            <div className="space-y-2">
              {currentMonthTasks.length === 0 ? (
                <div className="bg-white rounded-lg p-8 border text-center">
                  <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">이 달에 예정된 활동이 없습니다.</p>
                </div>
              ) : (
                currentMonthTasks.map((task) => {
                  const status = getTaskStatus(task);
                  const StatusIcon = status.icon;
                  const progress = Math.min(100, Math.max(0, Number(task.progress) || 0));

                  return (
                    <button
                      key={task.id}
                      onClick={() => handleActivityClick(task)}
                      className={`w-full text-left bg-white rounded-xl p-4 border-2 transition-all active:scale-[0.98] ${task.is_critical
                          ? "border-l-4 border-l-red-500 border-red-200"
                          : "border-gray-100 hover:border-blue-200"
                        }`}
                    >
                      {/* 상단: 제목 + 상태 */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-semibold truncate ${task.is_critical ? "text-red-700" : "text-gray-900"}`}>
                            {task.name}
                          </h3>
                          <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                            {formatDate(task.start)} ~ {formatDate(task.end)}
                          </p>
                        </div>
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${status.bgColor} ${status.textColor}`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                      </div>

                      {/* 진행률 바 */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="text-gray-500">진행률</span>
                          <span className="font-semibold text-gray-700">{progress}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${status.color}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      {/* Critical Path 표시 */}
                      {task.is_critical && (
                        <div className="flex items-center gap-1 text-[10px] text-red-600 mt-2">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Critical Path</span>
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </>
        ) : (
          /* 기존 간트 뷰 (축소) */
          <div className="bg-white rounded-lg border overflow-hidden">
            {/* 간트 뷰 모드 선택 */}
            <div className="p-2 border-b bg-gray-50">
              <div className="grid grid-cols-4 gap-1">
                {["Day", "Week", "Month", "Year"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setGanttViewMode(mode)}
                    className={`py-1.5 rounded text-[11px] font-medium transition-all ${ganttViewMode === mode
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-600 border"
                      }`}
                  >
                    {mode === "Day" ? "일간" : mode === "Week" ? "주간" : mode === "Month" ? "월간" : "연간"}
                  </button>
                ))}
              </div>
            </div>

            {/* 간트 차트 */}
            <div className="p-2 text-center text-xs text-blue-600 bg-blue-50 border-b">
              📱 확대 버튼으로 전체 보기
            </div>
            <div className="overflow-x-auto" style={{ maxHeight: "50vh" }}>
              <div
                ref={ganttRef}
                className="gantt-wrapper"
                style={{
                  minHeight: Math.max(200, data.length * 24 + 40),
                  padding: '6px',
                  width: 'max-content',
                  minWidth: '400px',
                }}
              />
            </div>
          </div>
        )}

        {/* 상세 패널 */}
        <SidePanel
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          title={selectedActivity?.name}
          width={320}
          side="right"
        >
          {selectedActivity && (
            <ActivityView
              activity={selectedActivity}
              ganttData={selectedActivityview}
            />
          )}
        </SidePanel>

        {/* 확대 모달 */}
        {isChartExpanded && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-3"
            onClick={() => setIsChartExpanded(false)}
          >
            <div
              className="w-full max-w-[98vw] bg-white rounded-xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-white" />
                  <h3 className="text-white font-semibold">공정표 확대</h3>
                </div>
                <button
                  onClick={() => setIsChartExpanded(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* 뷰 모드 */}
              <div className="bg-gray-50 px-4 py-2 border-b">
                <div className="flex gap-2">
                  {["Day", "Week", "Month", "Year"].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setGanttViewMode(mode)}
                      className={`px-3 py-1.5 rounded text-xs font-medium ${ganttViewMode === mode
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-600 border"
                        }`}
                    >
                      {mode === "Day" ? "일간" : mode === "Week" ? "주간" : mode === "Month" ? "월간" : "연간"}
                    </button>
                  ))}
                </div>
              </div>

              {/* 확대 간트 */}
              <div className="p-4 overflow-auto" style={{ maxHeight: "80vh" }}>
                <div
                  ref={expandedGanttRef}
                  className="gantt-wrapper"
                  style={{
                    minHeight: Math.max(350, data.length * 32 + 80),
                    padding: '10px',
                    width: 'max-content',
                    minWidth: '600px',
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Construction;
